import { Dispatch, RefObject, SetStateAction, useState } from 'react';
import {
  Filter,
  HandleMassSelectArgs,
  HandleSelectedArgs,
  Region,
} from './types';
import Checkbox from './checkbox';
import clsx from 'clsx';
import Accordion from '../accordion';
import { pressedEnter } from '@react/utils';

export function RegionMenu({
  regions,
  locations,
  buttonSection,
  handleSelected,
  handleMassSelect,
}: Readonly<{
  regions: Region[];
  locations: Filter;
  buttonSection: React.ReactNode;
  handleSelected: HandleSelectedArgs;
  handleMassSelect: HandleMassSelectArgs;
}>) {
  const [selectedRegion, setSelectedRegion] = useState<string>(undefined);
  const contries = regions?.find(
    item => item.region.id === selectedRegion
  )?.countries;
  const countryIds = contries?.map(item => item.id);
  const shouldShowButtons = contries?.length;

  if (!regions?.length) return null;

  return (
    <div className="filters__panel-split">
      <ul className="filters__panel-side">
        {regions?.map(region => {
          const isOpen = region.region.id === selectedRegion;
          const countryIds = region.countries.map(item => item.id);
          const facets = locations.facets.filter(facet =>
            countryIds.includes(facet.id)
          );
          const facetIds = facets.map(facet => facet.id);
          const allSelected = facets.every(item => item.selected);

          if (!facetIds.length) return null;

          const handleSelectAll = () => {
            handleMassSelect(locations.name, facetIds, !allSelected);
            setSelectedRegion(region.region.id);
          };

          return (
            <li
              className={clsx('filters__panel-side-item', {
                open: isOpen,
              })}
              key={region.region.id}
            >
              <label className="filters__input-wrapper">
                <input
                  className="filters__input"
                  type="checkbox"
                  title="Select all"
                  checked={allSelected}
                  onChange={handleSelectAll}
                  onKeyDown={e => pressedEnter(e, handleSelectAll)}
                />
              </label>
              <button
                className="filters__panel-side-button"
                onClick={() => setSelectedRegion(region.region.id)}
              >
                <span>{region.region.name}</span>
              </button>
            </li>
          );
        })}
      </ul>
      <div className="filters__panel-inner">
        <ul className="filters__panel-list filters__panel-list--3-cols">
          {selectedRegion &&
            locations.facets?.map(facet => {
              // Remove locations that cannot be selected
              if (!countryIds.includes(facet.id)) return null;

              return (
                <Checkbox
                  key={`checkbox-item-${facet.id}`}
                  facet={facet}
                  callback={() => handleSelected(facet.id)}
                />
              );
            })}
        </ul>
        {shouldShowButtons ? buttonSection : null}
      </div>
    </div>
  );
}

export function RegionMenuMobile({
  filter,
  regions,
  selectedIndex,
  openAccordion,
  scrollableParent,
  setOpenAccordion,
  handleSelected,
  handleMassSelect,
}: Readonly<{
  filter: Filter;
  regions: Region[];
  selectedIndex: number;
  openAccordion: string;
  scrollableParent: RefObject<HTMLElement>;
  handleSelected: HandleSelectedArgs;
  handleMassSelect: HandleMassSelectArgs;
  setOpenAccordion: Dispatch<SetStateAction<string>>;
}>) {
  const [height, setHeight] = useState({});
  const updateHeight = (id: string, height: number) => {
    setHeight(prev => ({ ...prev, [id]: height }));
  };

  const getHeight = () => {
    const vals: number[] = Object.values(height);
    if (!vals.length) return 0;

    return vals.reduce((acc, cur) => acc + cur, 0);
  };

  if (!regions.length) return null;

  return (
    <Accordion
      className="vertical-accordion--has-nested"
      title={`${filter.name} ${selectedIndex ? '(' + selectedIndex + ')' : ''}`}
      key={`accordion-${filter.name}`}
      onClick={setOpenAccordion}
      close={filter.name !== openAccordion}
      surplusHeight={getHeight()}
      scrollableParent={scrollableParent}
    >
      {regions?.map(region => {
        const countryIds = region.countries.map(item => item.id);
        const facets = filter.facets.filter(facet =>
          countryIds.includes(facet.id)
        );
        const facetIds = facets.map(facet => facet.id);
        const allSelected = facets.every(item => item.selected);

        if (!facetIds.length) return null;

        const handleSelectAll = () => {
          handleMassSelect(filter.name, facetIds, !allSelected);
        };

        return (
          <Accordion
            className="vertical-accordion--sub"
            checkbox={
              <input
                className="filters__input"
                type="checkbox"
                title="Select all"
                checked={allSelected}
                onChange={handleSelectAll}
                onKeyDown={e => pressedEnter(e, handleSelectAll)}
              />
            }
            scrollableParent={scrollableParent}
            title={region.region.name}
            key={region.region.id}
            setHeight={updateHeight}
            id={region.region.id}
          >
            <ul className="filter-menu__list filter-menu__list--nested">
              {filter.facets.map(facet => {
                if (!countryIds.includes(facet.id)) return null;

                return (
                  <Checkbox
                    key={`checkbox-item-${facet.id}`}
                    facet={facet}
                    callback={() => handleSelected(facet.id)}
                  />
                );
              })}
            </ul>
          </Accordion>
        );
      })}
    </Accordion>
  );
}
